import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Listbox } from '@/components/ui/listbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { formatDateTime } from '@/utils/format';

import { PastoralSubnav } from './pastoral-subnav';
import { useMemberPicker } from './use-member-picker';

const APPOINTMENT_KINDS = ['visit', 'counseling', 'general', 'follow_up'] as const;
type AppointmentKind = (typeof APPOINTMENT_KINDS)[number];

interface AppointmentRow {
  id: string;
  memberId: string;
  withMemberId: string;
  kind: AppointmentKind;
  startsAt: string;
  endsAt: string;
  purpose: string | null;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
}

export const AppointmentsPage = () => {
  const { t } = useTranslation('pastoral');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canManage = hasPermission(membership, PERMISSIONS.PASTORAL_MANAGE_APPOINTMENTS);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [open, setOpen] = useState(false);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [withMemberId, setWithMemberId] = useState<string | null>(null);
  const [startsAt, setStartsAt] = useState<string | null>(null);
  const [endsAt, setEndsAt] = useState<string | null>(null);
  const [kind, setKind] = useState<AppointmentKind>('general');
  const [purpose, setPurpose] = useState('');

  const memberPicker = useMemberPicker(open, 'pastoral-appointment-member');
  const withPicker = useMemberPicker(open, 'pastoral-appointment-with');

  const list = useQuery({
    queryKey: ['pastoral', 'appointments', page, pageSize],
    queryFn: () =>
      api.get<{ data: AppointmentRow[]; page: OffsetPage }>('/pastoral/appointments', {
        query: { page, pageSize },
      }),
    placeholderData: keepPreviousData,
  });

  const reset = () => {
    setMemberId(null);
    setWithMemberId(null);
    setStartsAt(null);
    setEndsAt(null);
    setKind('general');
    setPurpose('');
    memberPicker.setSelected(null);
    withPicker.setSelected(null);
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<AppointmentRow>('/pastoral/appointments', {
        memberId,
        withMemberId,
        startsAt,
        endsAt,
        kind,
        purpose: purpose.trim() || null,
      }),
    onSuccess: async () => {
      toast.success(t('appointments.saved'));
      setOpen(false);
      reset();
      await queryClient.invalidateQueries({ queryKey: ['pastoral', 'appointments'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const columns: DataTableColumn<AppointmentRow>[] = [
    {
      id: 'startsAt',
      header: t('appointments.columns.startsAt'),
      cell: (row) => formatDateTime(row.startsAt),
    },
    {
      id: 'kind',
      header: t('appointments.columns.kind'),
      cell: (row) => t(`appointments.kind.${row.kind}`),
    },
    {
      id: 'purpose',
      header: t('appointments.columns.purpose'),
      cell: (row) => row.purpose || '—',
    },
    {
      id: 'status',
      header: t('appointments.columns.status'),
      cell: (row) => (
        <Badge variant="neutral">{t(`appointments.status.${row.status}`)}</Badge>
      ),
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;
  const canSubmit = Boolean(memberId && withMemberId && startsAt && endsAt);

  return (
    <div>
      <PageHeader
        title={t('appointments.title')}
        description={t('appointments.description')}
        headerActions={
          canManage ? (
            <Button onClick={() => setOpen(true)}>{t('appointments.create')}</Button>
          ) : null
        }
      />

      <PastoralSubnav />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={
          <div className="space-y-3 py-4 text-center">
            <p className="text-body font-medium">{t('appointments.empty.title')}</p>
            <p className="text-body-sm text-fg-secondary">{t('appointments.empty.description')}</p>
            {canManage ? (
              <div className="flex justify-end sm:justify-center">
                <Button onClick={() => setOpen(true)}>{t('appointments.empty.action')}</Button>
              </div>
            ) : null}
          </div>
        }
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <FormDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) reset();
        }}
        title={t('appointments.create')}
        submitLabel={t('appointments.save')}
        cancelLabel={tCommon('action.cancel')}
        submitting={create.isPending}
        submitDisabled={!canSubmit}
        size="lg"
        onSubmit={() => create.mutate()}
      >
        <FormField label={t('appointments.fields.member')} required>
          {(field) => (
            <EntityCombobox
              id={field.id}
              value={memberId}
              onChange={(id) => {
                setMemberId(id);
                memberPicker.select(id);
              }}
              options={memberPicker.options}
              selected={memberPicker.selected}
              onQueryChange={memberPicker.setQuery}
              loading={memberPicker.loading}
              placeholder={t('memberPicker.placeholder')}
              emptyMessage={t('memberPicker.empty')}
              clearLabel={tCommon('action.clearSelection')}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('appointments.fields.withMember')} required>
          {(field) => (
            <EntityCombobox
              id={field.id}
              value={withMemberId}
              onChange={(id) => {
                setWithMemberId(id);
                withPicker.select(id);
              }}
              options={withPicker.options}
              selected={withPicker.selected}
              onQueryChange={withPicker.setQuery}
              loading={withPicker.loading}
              placeholder={t('memberPicker.placeholder')}
              emptyMessage={t('memberPicker.empty')}
              clearLabel={tCommon('action.clearSelection')}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('appointments.fields.startsAt')} required>
          {(field) => (
            <DateTimePicker
              id={field.id}
              value={startsAt}
              onChange={setStartsAt}
              popoverPortal="dialog"
              timezoneNotice={(timezone) =>
                t('appointments.fields.timezoneNotice', { timezone })
              }
            />
          )}
        </FormField>

        <FormField label={t('appointments.fields.endsAt')} required>
          {(field) => (
            <DateTimePicker
              id={field.id}
              value={endsAt}
              onChange={setEndsAt}
              popoverPortal="dialog"
              timezoneNotice={(timezone) =>
                t('appointments.fields.timezoneNotice', { timezone })
              }
            />
          )}
        </FormField>

        <FormField label={t('appointments.fields.kind')} required>
          {(field) => (
            <Listbox
              id={field.id}
              value={kind}
              onChange={(value) => setKind((value as AppointmentKind) ?? 'general')}
              options={APPOINTMENT_KINDS.map((value) => ({
                value,
                label: t(`appointments.kind.${value}`),
              }))}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('appointments.fields.purpose')}>
          {(field) => (
            <Textarea
              id={field.id}
              value={purpose}
              onChange={(event) => setPurpose(event.target.value)}
              rows={3}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>
      </FormDialog>
    </div>
  );
};
